import React from 'react';
import axios from 'axios';
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

// Global axios interceptor: auto-attach token + handle 401 expiry across the app
axios.interceptors.request.use((config) => {
  // Don't override Authorization if caller already set one (admin vs portal flows)
  if (!config.headers?.Authorization) {
    const token = localStorage.getItem('token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});
axios.interceptors.response.use(
  (r) => r,
  (err) => {
    const path = window.location.pathname;
    const isPublic =
      path.startsWith('/clock/') ||
      path.startsWith('/login') ||
      path.startsWith('/employee-portal');
    if (err.response?.status === 401 && !isPublic) {
      // Token expired — clear and redirect (skip public/portal pages)
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login?expired=1';
    }
    return Promise.reject(err);
  }
);

import Dashboard from './pages/Dashboard';
import Employees from './pages/Employees';
import Leaves from './pages/Leaves';
import LeaveCalendar from './pages/LeaveCalendar';
import Attendance from './pages/Attendance';
import Settings from './pages/Settings';
import EmailLog from './pages/EmailLog';
import HRReport from './pages/HRReport';
import TicketTracker from './pages/TicketTracker';
import TicketReminders from './pages/TicketReminders';
import NewsDesk from './pages/NewsDesk';
import LeaveForecast from './pages/LeaveForecast';
import SalaryRevisions from './pages/SalaryRevisions';import PublicHolidays from './pages/PublicHolidays';
import VisaHolders from './pages/VisaHolders';
import StaffVault from './pages/StaffVault';
import EOSBLedger from './pages/EOSBLedger';
import CompanyDocuments from './pages/CompanyDocuments';
import KPI from './pages/KPI';
import OfferLetters from './pages/OfferLetters';
import WarningLetters from './pages/WarningLetters';
import CompanyAssets from './pages/CompanyAssets';
import Onboarding from './pages/Onboarding';
import Offboarding from './pages/Offboarding';
import Invoices from './pages/Invoices';
import PartTimers from './pages/PartTimers';
import Logbook from './pages/Logbook';
import DctLicensing from './pages/DctLicensing';
import Appraisals from './pages/Appraisals';
import OrgChart from './pages/OrgChart';
import HandbookManager from './pages/HandbookManager';
import MemoManager from './pages/MemoManager';
import InsuranceCoverageAdmin from './pages/InsuranceCoverageAdmin';
import ExperienceCertificate from './pages/ExperienceCertificate';
import Discipline from './pages/Discipline';
import SalaryCertificate from './pages/SalaryCertificate';
import NOCCertificate from './pages/NOCCertificate';
import DocumentRepository from './pages/DocumentRepository';
import BulkEmail from './pages/BulkEmail';
import SalaryAdvances from './pages/SalaryAdvances';
import BrandingSettings from './pages/BrandingSettings';
import ClockAdmin from './pages/ClockAdmin';
// Lazy-load the clock station so its heavy face-api.js (~6MB) dependency is
// NOT bundled into the main app — this was bloating every page load and
// causing the "Loading…" hang on mobile/slow connections at the venue.
const ClockStation = React.lazy(() => import('./pages/ClockStation'));
import AppQR from './pages/AppQR';
import LeaveApproval from './pages/LeaveApproval';
import SettlementConfirm from './pages/SettlementConfirm';
import ClockDisplay from './pages/ClockDisplay';
import UrgentAlerts from './pages/UrgentAlerts';
import TodoHistory from './pages/TodoHistory';
import GrievanceManager from './pages/GrievanceManager';
import Login from './pages/Login';
import Layout from './components/Layout';
import { Toaster } from './components/ui/toaster';

// Employee Portal
import PortalLogin from './pages/employee-portal/PortalLogin';
import PortalDashboard from './pages/employee-portal/PortalDashboard';
import PortalProfile from './pages/employee-portal/PortalProfile';
import PortalLeaves from './pages/employee-portal/PortalLeaves';
import PortalMyTeam from './pages/employee-portal/PortalMyTeam';
import PortalLeaveCalendar from './pages/employee-portal/PortalLeaveCalendar';
import PortalNewJoiners from './pages/employee-portal/PortalNewJoiners';
import PortalPayslips from './pages/employee-portal/PortalPayslips';
import PortalLeaveSalary from './pages/employee-portal/PortalLeaveSalary';
import PortalHandbook from './pages/employee-portal/PortalHandbook';
import PortalDocuments from './pages/employee-portal/PortalDocuments';
import PortalAssets from './pages/employee-portal/PortalAssets';
import PortalPayroll from './pages/employee-portal/PortalPayroll';
import PortalTodos from './pages/employee-portal/PortalTodos';
import PortalTeamTodos from './pages/employee-portal/PortalTeamTodos';
import PortalManagerWarnings from './pages/employee-portal/PortalManagerWarnings';
import PortalLeaveForecast from './pages/employee-portal/PortalLeaveForecast';
import PortalGrievance from './pages/employee-portal/PortalGrievance';
import PortalNewsDesk from './pages/employee-portal/PortalNewsDesk';
import PortalOffboarding from './pages/employee-portal/PortalOffboarding';
import PortalAppraisal from './pages/employee-portal/PortalAppraisal';
import PortalMemos from './pages/employee-portal/PortalMemos';
import PortalInsurance from './pages/employee-portal/PortalInsurance';
import PortalMyLetters from './pages/employee-portal/PortalMyLetters';
import PortalWarningLetters from './pages/employee-portal/PortalWarningLetters';
import PortalSalaryAdvance from './pages/employee-portal/PortalSalaryAdvance';
import PortalClockPin from './pages/employee-portal/PortalClockPin';
import PortalClockIn from './pages/employee-portal/PortalClockIn';
import PortalClockRecords from './pages/employee-portal/PortalClockRecords';
import PortalLogbook from './pages/employee-portal/PortalLogbook';
import PortalEOSB from './pages/employee-portal/PortalEOSB';
import PortalLayout from './components/employee-portal/PortalLayout';

// Protected Route Component (Admin)
const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem('token');
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

// Protected Route (Employee Portal)
const PortalProtectedRoute = ({ children }) => {
  const token = localStorage.getItem('portal_token');
  if (!token) {
    return <Navigate to="/employee-portal/login" replace />;
  }
  return <PortalLayout>{children}</PortalLayout>;
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <React.Suspense fallback={<div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100vh',color:'#6b7280',fontFamily:'sans-serif'}}>Loading…</div>}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/leave-approval" element={<LeaveApproval />} />
          <Route path="/settlement-confirm/:token" element={<SettlementConfirm />} />
          <Route path="/clock-display/:token" element={<ClockDisplay />} />
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Layout>
                  <Dashboard />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/employees"
            element={
              <ProtectedRoute>
                <Layout>
                  <Employees />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/leaves"
            element={
              <ProtectedRoute>
                <Layout>
                  <Leaves />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/leave-calendar"
            element={
              <ProtectedRoute>
                <Layout>
                  <LeaveCalendar />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/public-holidays"
            element={
              <ProtectedRoute>
                <Layout>
                  <PublicHolidays />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/visa-holders"
            element={
              <ProtectedRoute>
                <Layout>
                  <VisaHolders />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/staff-vault"
            element={
              <ProtectedRoute>
                <Layout>
                  <StaffVault />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/eosb"
            element={
              <ProtectedRoute>
                <Layout>
                  <EOSBLedger />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/attendance"
            element={
              <ProtectedRoute>
                <Layout>
                  <Attendance />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/hr-report"
            element={
              <ProtectedRoute>
                <Layout>
                  <HRReport />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/ticket-tracker"
            element={
              <ProtectedRoute>
                <Layout>
                  <TicketTracker />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/ticket-reminders"
            element={
              <ProtectedRoute>
                <Layout>
                  <TicketReminders />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/urgent-alerts"
            element={
              <ProtectedRoute>
                <Layout>
                  <UrgentAlerts />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/news-desk"
            element={
              <ProtectedRoute>
                <Layout>
                  <NewsDesk />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/leave-forecast"
            element={
              <ProtectedRoute>
                <Layout>
                  <LeaveForecast />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/salary-revisions"
            element={
              <ProtectedRoute>
                <Layout>
                  <SalaryRevisions />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/company-documents"
            element={
              <ProtectedRoute>
                <Layout>
                  <CompanyDocuments />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/kpi"
            element={
              <ProtectedRoute>
                <Layout>
                  <KPI />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Layout>
                  <Settings />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/email-log"
            element={
              <ProtectedRoute>
                <Layout>
                  <EmailLog />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/offer-letters"
            element={
              <ProtectedRoute>
                <Layout>
                  <OfferLetters />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/warning-letters"
            element={
              <ProtectedRoute>
                <Layout>
                  <WarningLetters />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/company-assets"
            element={
              <ProtectedRoute>
                <Layout>
                  <CompanyAssets />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/onboarding"
            element={
              <ProtectedRoute>
                <Layout>
                  <Onboarding />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/offboarding"
            element={
              <ProtectedRoute>
                <Layout>
                  <Offboarding />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/invoices"
            element={
              <ProtectedRoute>
                <Layout>
                  <Invoices />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/part-timers"
            element={
              <ProtectedRoute>
                <Layout>
                  <PartTimers />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/logbook"
            element={
              <ProtectedRoute>
                <Layout>
                  <Logbook />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dct-licensing"
            element={
              <ProtectedRoute>
                <Layout>
                  <DctLicensing />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/appraisals"
            element={
              <ProtectedRoute>
                <Layout>
                  <Appraisals />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/org-chart"
            element={
              <ProtectedRoute>
                <Layout>
                  <OrgChart />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/employee-handbook"
            element={
              <ProtectedRoute>
                <Layout>
                  <HandbookManager />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/company-memos"
            element={
              <ProtectedRoute>
                <Layout>
                  <MemoManager />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/insurance-coverage"
            element={
              <ProtectedRoute>
                <Layout>
                  <InsuranceCoverageAdmin />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/experience-certificate"
            element={
              <ProtectedRoute>
                <Layout>
                  <ExperienceCertificate />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/salary-certificate"
            element={
              <ProtectedRoute>
                <Layout>
                  <SalaryCertificate />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/noc-certificate"
            element={
              <ProtectedRoute>
                <Layout>
                  <NOCCertificate />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/document-repository"
            element={
              <ProtectedRoute>
                <Layout>
                  <DocumentRepository />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/discipline"
            element={
              <ProtectedRoute>
                <Layout>
                  <Discipline />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/salary-advances"
            element={
              <ProtectedRoute>
                <Layout>
                  <SalaryAdvances />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/branding"
            element={
              <ProtectedRoute>
                <Layout>
                  <BrandingSettings />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/clock-in-out"
            element={
              <ProtectedRoute>
                <Layout>
                  <ClockAdmin />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/bulk-email"
            element={
              <ProtectedRoute>
                <Layout>
                  <BulkEmail />
                </Layout>
              </ProtectedRoute>
            }
          />
          {/* Public clock station page (no auth) */}
          <Route path="/clock/:qrToken" element={<ClockStation />} />
          <Route
            path="/app-qr"
            element={
              <ProtectedRoute>
                <Layout>
                  <AppQR />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/todo-history"
            element={
              <ProtectedRoute>
                <Layout>
                  <TodoHistory />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/grievances"
            element={
              <ProtectedRoute>
                <Layout>
                  <GrievanceManager />
                </Layout>
              </ProtectedRoute>
            }
          />
          {/* Employee Portal Routes */}
          <Route path="/employee-portal/login" element={<PortalLogin />} />
          <Route path="/employee-portal" element={<PortalProtectedRoute><PortalDashboard /></PortalProtectedRoute>} />
          <Route path="/employee-portal/profile" element={<PortalProtectedRoute><PortalProfile /></PortalProtectedRoute>} />
          <Route path="/employee-portal/leaves" element={<PortalProtectedRoute><PortalLeaves /></PortalProtectedRoute>} />
          <Route path="/employee-portal/my-team" element={<PortalProtectedRoute><PortalMyTeam /></PortalProtectedRoute>} />
          <Route path="/employee-portal/leave-calendar" element={<PortalProtectedRoute><PortalLeaveCalendar /></PortalProtectedRoute>} />
          <Route path="/employee-portal/new-joiners" element={<PortalProtectedRoute><PortalNewJoiners /></PortalProtectedRoute>} />
          <Route path="/employee-portal/payslips" element={<PortalProtectedRoute><PortalPayslips /></PortalProtectedRoute>} />
          <Route path="/employee-portal/leave-salary" element={<PortalProtectedRoute><PortalLeaveSalary /></PortalProtectedRoute>} />
          <Route path="/employee-portal/handbook" element={<PortalProtectedRoute><PortalHandbook /></PortalProtectedRoute>} />
          <Route path="/employee-portal/eosb" element={<PortalProtectedRoute><PortalEOSB /></PortalProtectedRoute>} />
          <Route path="/employee-portal/documents" element={<PortalProtectedRoute><PortalDocuments /></PortalProtectedRoute>} />
          <Route path="/employee-portal/assets" element={<PortalProtectedRoute><PortalAssets /></PortalProtectedRoute>} />
          <Route path="/employee-portal/payroll" element={<PortalProtectedRoute><PortalPayroll /></PortalProtectedRoute>} />
          <Route path="/employee-portal/todos" element={<PortalProtectedRoute><PortalTodos /></PortalProtectedRoute>} />
          <Route path="/employee-portal/team-todos" element={<PortalProtectedRoute><PortalTeamTodos /></PortalProtectedRoute>} />
          <Route path="/employee-portal/manager-warnings" element={<PortalProtectedRoute><PortalManagerWarnings /></PortalProtectedRoute>} />
          <Route path="/employee-portal/leave-forecast" element={<PortalProtectedRoute><PortalLeaveForecast /></PortalProtectedRoute>} />
          <Route path="/employee-portal/message-hr" element={<PortalProtectedRoute><PortalGrievance /></PortalProtectedRoute>} />
          <Route path="/employee-portal/news-desk" element={<PortalProtectedRoute><PortalNewsDesk /></PortalProtectedRoute>} />
          <Route path="/employee-portal/offboarding" element={<PortalProtectedRoute><PortalOffboarding /></PortalProtectedRoute>} />
          <Route path="/employee-portal/appraisal" element={<PortalProtectedRoute><PortalAppraisal /></PortalProtectedRoute>} />
          <Route path="/employee-portal/memos" element={<PortalProtectedRoute><PortalMemos /></PortalProtectedRoute>} />
          <Route path="/employee-portal/insurance" element={<PortalProtectedRoute><PortalInsurance /></PortalProtectedRoute>} />
          <Route path="/employee-portal/my-letters" element={<PortalProtectedRoute><PortalMyLetters /></PortalProtectedRoute>} />
          <Route path="/employee-portal/warning-letters" element={<PortalProtectedRoute><PortalWarningLetters /></PortalProtectedRoute>} />
          <Route path="/employee-portal/salary-advance" element={<PortalProtectedRoute><PortalSalaryAdvance /></PortalProtectedRoute>} />
          <Route path="/employee-portal/clock-pin" element={<PortalProtectedRoute><PortalClockPin /></PortalProtectedRoute>} />
          <Route path="/employee-portal/clock-in" element={<PortalProtectedRoute><PortalClockIn /></PortalProtectedRoute>} />
          <Route path="/employee-portal/clock-records" element={<PortalProtectedRoute><PortalClockRecords /></PortalProtectedRoute>} />
          <Route path="/employee-portal/logbook" element={<PortalProtectedRoute><PortalLogbook /></PortalProtectedRoute>} />
        </Routes>
        </React.Suspense>
      </BrowserRouter>
      <Toaster />
    </div>
  );
}

export default App;
